import React, { useState, useEffect } from 'react';
import api from '../services/api';

const LogManagementPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(10);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sortBy, setSortBy] = useState('entityType');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await api.get('/logs');
        setLogs(res.data || []);
      } catch (err) {
        console.error('Error fetching logs:', err);
        setError(err.message);
        setLogs([]); // Set empty array as fallback
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const clearAllLogs = async () => {
    setClearing(true);
    try {
      await api.delete('/logs');
      setLogs([]);
      setShowClearConfirm(false);
      console.log('✅ All logs cleared successfully');
      // Show success message (you could add a toast notification here)
    } catch (err) {
      console.error('❌ Error clearing logs:', err);
      setError('Failed to clear logs: ' + err.message);
    } finally {
      setClearing(false);
    }
  };

  const confirmClearLogs = () => {
    setShowClearConfirm(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No Date';
    
    try {
      let date;
      
      // Handle different date formats
      if (typeof dateString === 'string') {
        // Handle MySQL date format YYYY-MM-DD HH:MM:SS
        date = new Date(dateString.replace(/\s/, 'T'));
        
        // If that doesn't work, try parsing directly
        if (isNaN(date.getTime())) {
          date = new Date(dateString);
        }
      } else if (typeof dateString === 'number') {
        // Handle timestamp
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return 'Invalid Date';
      }
      
      // Format date with better formatting
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateString);
      return 'Format Error';
    }
  };

  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case 'create':
      case 'created':
        return 'bg-green-100 text-green-800';
      case 'update':
      case 'updated':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
      case 'deleted':
        return 'bg-red-100 text-red-800';
      case 'login':
        return 'bg-purple-100 text-purple-800';
      case 'logout':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType?.toLowerCase()) {
      case 'user':
        return '👤';
      case 'course':
        return '📚';
      case 'quiz':
        return '📝';
      case 'submission':
        return '📊';
      case 'question':
        return '❓';
      default:
        return '📋';
    }
  };

  // Filter and sort logs
  const filteredAndSortedLogs = logs
    .filter(log => {
      const matchesEntity = log.entityType?.toLowerCase().includes(filter.toLowerCase()) ||
                           log.entityId?.toString().includes(filter);
      const matchesAction = actionFilter === '' || log.action?.toLowerCase() === actionFilter.toLowerCase();
      return matchesEntity && matchesAction;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt || a.timestamp || 0);
          bValue = new Date(b.createdAt || b.timestamp || 0);
          break;
        case 'entityType':
          aValue = (a.entityType || '').toLowerCase();
          bValue = (b.entityType || '').toLowerCase();
          break;
        case 'action':
          aValue = (a.action || '').toLowerCase();
          bValue = (b.action || '').toLowerCase();
          break;
        case 'entityId':
          aValue = a.entityId || 0;
          bValue = b.entityId || 0;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  // Pagination
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredAndSortedLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(filteredAndSortedLogs.length / logsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Logs</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <span className="text-4xl">📋</span>
                <span>Audit Logs</span>
              </h1>
              <p className="text-gray-600 mt-1">Track all system activities and changes</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium">
                {filteredAndSortedLogs.length} logs found
              </div>
              <button
                onClick={confirmClearLogs}
                disabled={logs.length === 0 || clearing}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <span>🗑️</span>
                <span>{clearing ? 'Clearing...' : 'Clear All Logs'}</span>
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <span>🔄</span>
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Filters & Sorting</h2>
          
          {/* Search and Action Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="entity-search" className="block text-sm font-medium text-gray-700 mb-2">Search Entity</label>
              <input
                id="entity-search"
                type="text"
                placeholder="Search by entity type or ID..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              />
            </div>
            <div>
              <label htmlFor="action-filter" className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
              <select
                id="action-filter"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </select>
            </div>
          </div>

          {/* Sorting Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="sort-by" className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              >
                <option value="entityType">Entity Type (Alphabetical)</option>
                <option value="action">Action (Alphabetical)</option>
                <option value="createdAt">Date & Time</option>
                <option value="entityId">Entity ID</option>
              </select>
            </div>
            <div>
              <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
              <select
                id="sort-order"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              >
                <option value="asc">Ascending (A-Z, Oldest First)</option>
                <option value="desc">Descending (Z-A, Newest First)</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilter('');
                  setActionFilter('');
                  setSortBy('entityType');
                  setSortOrder('asc');
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
              >
                Reset All
              </button>
            </div>
          </div>
        </div>

        {/* Logs Display */}
        {currentLogs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Logs Found</h3>
            <p className="text-gray-600">No audit logs match your current filters.</p>
          </div>
        ) : (
          <>
            {/* Table View */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entity
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentLogs.map((log, index) => (
                      <tr 
                        key={log.id || Math.random()} 
                        className={`hover:bg-gray-50 transition-colors duration-200 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <span className="text-2xl">{getEntityIcon(log.entityType)}</span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {log.entityType || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {log.entityId || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                            {log.action || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(log.createdAt || log.timestamp)}
                        </td>
                        <td className="px-6 py-4">
                          {(log.before || log.after) ? (
                            <div className="space-y-2">
                              {log.before && (
                                <div>
                                  <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded">
                                    Before
                                  </span>
                                  <div className="mt-1 text-xs text-gray-600 max-w-xs truncate">
                                    {JSON.stringify(log.before)}
                                  </div>
                                </div>
                              )}
                              {log.after && (
                                <div>
                                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                                    After
                                  </span>
                                  <div className="mt-1 text-xs text-gray-600 max-w-xs truncate">
                                    {JSON.stringify(log.after)}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No changes recorded</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                      currentPage === page
                        ? 'bg-blue-500 text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Clear Logs Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 transform transition-all duration-300 scale-100">
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Clear All Logs</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to clear all audit logs? This action cannot be undone and will permanently delete all log records.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  disabled={clearing}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={clearAllLogs}
                  disabled={clearing}
                  className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center space-x-2"
                >
                  <span>🗑️</span>
                  <span>{clearing ? 'Clearing...' : 'Clear All Logs'}</span>
                </button>
              </div>
              {clearing && (
                <div className="mt-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Clearing logs...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogManagementPage;
